/**
 * 价格套餐弹窗组件
 * 在 Header 中点击 Upgrade 按钮时弹出
 * 与 Pricing 页面的套餐卡片完全一致
 */

'use client';

import { recordPurchaseReceipt, rememberUpgradeInvoice, rememberCheckoutSession, redirectToCheckout } from '@/utils/purchase-return';
import { isPaymentVerificationCancelledError, paymentFetch } from '@/lib/payment-risk/client';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import PricingPlansSection from '@/components/PricingPlansSection';
import PricingFAQ from '@/components/PricingFAQ';
import PricingShowcaseHeader from '@/components/PricingShowcaseHeader';
import { getClient } from '@/lib/supabase/client';
import {
  fetchCachedApi,
  CLIENT_CACHE_KEYS,
  invalidateClientCache,
  notifyCreditsUpdated,
  notifySubscriptionUpdated,
} from '@/lib/client-request-cache';
import { computePlanButtonState } from '@/utils/pricingButtonState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ToastProvider';
import { useLocale, useTranslations } from 'next-intl';
import {
  PRICING_PLAN_CONTENT,
  PRICING_PLAN_ORDER,
} from '@/config/pricing-content';
import { useCampaignConfigs } from '@/hooks/useCampaignConfigs';

export default function PricingModal({ isOpen, onClose }) {
  const locale = useLocale();
  const { pricingSpotlight } = useCampaignConfigs();
  const t = useTranslations('Pricing');
  const tToasts = useTranslations('Toasts'); // ✅ Added hook for Toasts
  const [billingCycle, setBillingCycle] = useState('yearly'); // 默认年度套餐
  const [pricingPlansData, setPricingPlansData] = useState(null);
  const [isPricingDataLoading, setIsPricingDataLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);

  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);
  const [user, setUser] = useState(null); // 用户登录状态
  const [mounted, setMounted] = useState(false); // Portal 依赖 document，需要等待客户端挂载
  const supabase = getClient();
  const { showToast } = useToast();
  const pricingPlans = useMemo(() => {
    const map = {};
    PRICING_PLAN_ORDER.forEach((planKey) => {
      const content = PRICING_PLAN_CONTENT[planKey];
      map[planKey] = {
        name: t(content.nameKey),
        ctaText: t(content.ctaKey),
      };
    });
    return map;
  }, [t]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // 当 PricingModal 打开时，硬性阻断全局原生地页面的滚动（不仅 body，包括 html，同时移除安全占位）
  useEffect(() => {
    if (!mounted || !isOpen) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
      document.documentElement.style.scrollbarGutter = '';
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      return undefined;
    }

    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlOverflowY = document.documentElement.style.overflowY;
    const originalHtmlGutter = document.documentElement.style.scrollbarGutter;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyOverflowY = document.body.style.overflowY;

    // 强制消除 html 和 body 级别的滚动，以及预留的原生滚动条占位
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overflowY = 'hidden';
    document.documentElement.style.scrollbarGutter = 'auto';
    document.body.style.overflow = 'hidden';
    document.body.style.overflowY = 'hidden';

    // 禁用 Radix Overlay
    const overlays = Array.from(
      document.querySelectorAll('[data-radix-dialog-overlay]')
    );

    const originalPointerEvents = overlays.map((overlay) => ({
      element: overlay,
      pointerEvents: overlay.style.pointerEvents || '',
    }));

    overlays.forEach((overlay) => {
      overlay.style.pointerEvents = 'none';
    });

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.overflowY = originalHtmlOverflowY;
      document.documentElement.style.scrollbarGutter = originalHtmlGutter;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.overflowY = originalBodyOverflowY;
      originalPointerEvents.forEach(({ element, pointerEvents }) => {
        element.style.pointerEvents = pointerEvents;
      });
    };
  }, [isOpen, mounted]);

  // 获取用户登录状态
  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
      } catch (error) {
        console.error('Error checking user:', error);
        setUser(null);
      }
    };

    if (isOpen) {
      checkUser();
    }
  }, [isOpen, supabase]);

  // ✅ 提取为 useCallback 函数：获取定价套餐数据
  const fetchPricingPlans = useCallback(async () => {
    try {
      setIsPricingDataLoading(true);
      const response = await fetchCachedApi('/api/pricing-plans', {
        cacheKey: CLIENT_CACHE_KEYS.pricingPlans,
        ttlMs: 60000,
      });
      const data = response.data || {};

      if (response.ok && data.success && data.plans) {
        const plansMap = {};
        data.plans.forEach(plan => {
          const planKey = plan.name.toLowerCase();
          plansMap[planKey] = {
            id: plan.id,
            stripePriceId: plan.stripePriceId,
            tier: plan.tier || 0,
            ...plan
          };
        });
        setPricingPlansData(plansMap);
      } else {
        setPricingPlansData({});
      }
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      setPricingPlansData({});
    } finally {
      setIsPricingDataLoading(false);
    }
  }, []);

  // ✅ 提取为 useCallback 函数：获取当前订阅信息
  const fetchCurrentSubscription = useCallback(async ({ force = false } = {}) => {
    try {
      setIsSubscriptionLoading(true);
      const response = await fetchCachedApi('/api/user/subscription', {
        cacheKey: CLIENT_CACHE_KEYS.subscription,
        ttlMs: 60000,
        force,
      });
      if (!response.ok) {
        setCurrentSubscription(null);
        return;
      }

      const data = response.data || {};
      if (data.success) {
        setCurrentSubscription(data.subscription);
      } else {
        setCurrentSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      setCurrentSubscription(null);
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, []);

  // 获取数据库中的定价套餐数据
  useEffect(() => {
    if (isOpen) {
      fetchPricingPlans();
    }
  }, [isOpen, fetchPricingPlans]);

  // 获取当前订阅信息
  useEffect(() => {
    if (isOpen) {
      fetchCurrentSubscription();
    }
  }, [isOpen, fetchCurrentSubscription]);

  // 获取按钮状态和文案
  const getButtonState = (planKey) =>
    computePlanButtonState({
      planKey,
      billingCycle,
      pricingPlans,
      pricingPlansData,
      currentSubscription,
      isSubscriptionLoading,
      isPricingDataLoading,
      isLoggedIn: !!user,
      texts: {
        currentPlan: t('currentPlan'),
        loading: t('processing'),
        upgrade: t('plans.plus.cta'),
        getStarted: t('plans.free.cta'),
      }
    });

  // ✅ 确认并执行升级
  const confirmAndUpgrade = useCallback(async () => {
    if (!selectedPlanForUpgrade) return;

    try {
      setProcessingPlan(selectedPlanForUpgrade.planKey);
      setShowUpgradeConfirm(false);

      const response = await paymentFetch('/api/subscriptions/upgrade-immediate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPlanId: selectedPlanForUpgrade.planId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upgrade subscription');
      }

      if (data.invoiceId) rememberUpgradeInvoice(data.invoiceId);
      if (data.sessionId) rememberCheckoutSession(data.sessionId);
      if (data.url) { await redirectToCheckout(data.url, 'subscription_upgrade', selectedPlanForUpgrade.planKey, data); return; }
      if (data.invoiceId) void recordPurchaseReceipt({invoiceId:data.invoiceId});

      showToast({
        title: tToasts('subscriptionUpgraded'),
        description: data.message || tToasts('subscriptionUpgradedDesc'),
        type: 'success',
      });

      invalidateClientCache(CLIENT_CACHE_KEYS.subscription);
      invalidateClientCache(CLIENT_CACHE_KEYS.credits);
      notifySubscriptionUpdated();
      notifyCreditsUpdated();

      // ✅ 刷新订阅数据
      await Promise.all([
        fetchCurrentSubscription({ force: true }),
        fetchPricingPlans(),
      ]);

      setProcessingPlan(null);
      setSelectedPlanForUpgrade(null);
      onClose(); // 关闭弹窗
      window.location.reload(); // 刷新页面以显示新状态
    } catch (error) {
      if (isPaymentVerificationCancelledError(error)) {
        setProcessingPlan(null);
        setSelectedPlanForUpgrade(null);
        return;
      }
      console.error('Error upgrading subscription:', error);
      showToast({
        title: tToasts('upgradeFailed'),
        description: error.message || tToasts('upgradeFailedDesc'),
        type: 'error',
      });
      setProcessingPlan(null);
      setSelectedPlanForUpgrade(null);
    }
  }, [selectedPlanForUpgrade, fetchCurrentSubscription, fetchPricingPlans, onClose, showToast, tToasts]);

  // 处理升级按钮点击
  const handleUpgrade = async (planKey) => {
    const buttonState = getButtonState(planKey);

    if (buttonState.action === 'free_cta') {
      onClose();
      return;
    }

    if (
      buttonState.disabled ||
      buttonState.action === 'none' ||
      buttonState.action === 'downgrade_blocked' ||
      buttonState.action === 'current' ||
      buttonState.action === 'loading'
    ) {
      return;
    }

    if (!buttonState.planId) {
      showToast({
        title: tToasts('error'),
        description: tToasts('planNotFound'),
        type: 'error',
      });
      return;
    }

    try {
      setProcessingPlan(planKey);

      // ✅ 立即升级（高于当前 tier）- 显示确认对话框
      if (buttonState.action === 'immediate_upgrade') {
        // ✅ 从 pricingPlansData 中查找计划名称（使用 Object.values）
        const targetPlan = pricingPlansData
          ? Object.values(pricingPlansData).find(p => p.id === buttonState.planId)
          : null;
        const planName = targetPlan?.name || planKey.charAt(0).toUpperCase() + planKey.slice(1);

        setSelectedPlanForUpgrade({
          planKey,
          planId: buttonState.planId,
          planName,
        });
        setShowUpgradeConfirm(true);
        setProcessingPlan(null);
        return;
      }

      // 首次订阅使用 checkout
      const response = await paymentFetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: buttonState.planId,
          locale, // ✅ Pass current locale to API
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.flow === 'upgrade' && data.invoiceId) rememberUpgradeInvoice(data.invoiceId);
      if (data.sessionId) rememberCheckoutSession(data.sessionId);
      if (data.url) {
        await redirectToCheckout(data.url, data.flow === 'upgrade' ? 'subscription_upgrade' : 'subscription_start', planKey, data);
        return;
      }

      if (data.flow === 'upgrade' && data.success) {
        if (data.invoiceId) void recordPurchaseReceipt({invoiceId:data.invoiceId});
        showToast({
          title: tToasts('subscriptionUpgraded'),
          description: data.message || tToasts('subscriptionUpgradedDesc'),
          type: 'success',
        });

        await Promise.all([
          fetchCurrentSubscription(),
          fetchPricingPlans(),
        ]);

        setProcessingPlan(null);
        onClose();
        window.location.reload();
        return;
      }

      throw new Error('No checkout URL received');
    } catch (error) {
      if (isPaymentVerificationCancelledError(error)) {
        setProcessingPlan(null);
        return;
      }
      console.error('Error processing upgrade:', error);
      showToast({
        title: tToasts('error'),
        description: tToasts('processUpgradeFailed', { error: error.message }),
        type: 'error',
      });
      setProcessingPlan(null);
    }
  };

  // 定价套餐数据由 PricingPlansSection 统一渲染

  // ✅ 修复：只在 isOpen 为 true 且组件挂载后渲染弹窗
  if (!isOpen || !mounted) return null;

  return createPortal(
    <div data-botgo-modal="pricing" className="fixed inset-0 z-[120] overflow-y-auto bg-black/50 pricing-modal-scrollbar" onClick={onClose}>

      {/* 弹窗内容的弹性承载区：保证内容大于屏幕时顶部底部都有安全间隙，自身不拦截点击事件 */}
      <div className="min-h-full flex flex-col items-center px-2 pb-10 pt-[88px] sm:px-4 sm:pb-16 sm:pt-[112px] pointer-events-none relative">

        {/* 实际面板区域：恢复完整的 28px 圆角和四周边框，打造悬浮卡片感。自然撑开。阻断点击以免关闭 modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 w-full max-w-[1384px] mx-auto rounded-[28px] border border-white/10 bg-[#2b2420] shadow-[0_40px_140px_rgba(0,0,0,0.72)] pointer-events-auto"
        >
          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            className="absolute right-5 top-5 z-20 rounded-full border border-white/8 bg-black/25 p-2 text-gray-300 backdrop-blur-sm transition-all duration-200 hover:border-amber-400/30 hover:bg-white/6 hover:text-white sm:right-6 sm:top-6"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="w-full pb-20">
            <PricingShowcaseHeader
              config={pricingSpotlight}
              variant="modal"
            />

            <PricingPlansSection
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              pricingPlansData={pricingPlansData}
              getButtonState={getButtonState}
              onSelectPlan={handleUpgrade}
              processingPlan={processingPlan}
              t={t}
              variant="modal"
              showTables={true}
              promoMeta={pricingSpotlight}
            />

            <PricingFAQ className="px-5 sm:px-8 max-w-4xl mx-auto mt-20" />
          </div>
        </div>
      </div>

      {/* ✅ 升级确认对话框 */}
      <AlertDialog open={showUpgradeConfirm} onOpenChange={setShowUpgradeConfirm}>
        <AlertDialogContent
          overlayClassName="z-[200]"
          className="z-[210] bg-neutral-900 border-neutral-800 w-[95vw] sm:max-w-[560px]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('upgradeConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              {t.rich('upgradeConfirm.description', {
                planName: selectedPlanForUpgrade?.planName,
                bold: (chunks) => <span className="text-amber-400 font-semibold">{chunks}</span>
              })}
              {(() => {
                const plansArray = pricingPlansData ? Object.values(pricingPlansData) : [];
                const newPlan = plansArray.find(p => p.id === selectedPlanForUpgrade?.planId);
                if (!newPlan) return null;
                const amount = `$${Number(newPlan.price || 0).toFixed(2)}`;
                const isYearly = newPlan.billingCycle === 'yearly';
                const periodText = isYearly ? t('upgradeConfirm.periodYearly') : t('upgradeConfirm.periodMonthly');
                const creditsUnit = isYearly
                  ? t('upgradeConfirm.creditsUnitYearly')
                  : t('upgradeConfirm.creditsUnitMonthly');
                return (
                  <>
                    <br /><br />
                    {t('upgradeConfirm.amount', { amount })}<br />
                    {t('upgradeConfirm.credits', { credits: newPlan.credits, creditsUnit })}<br />
                    {t('upgradeConfirm.period', { periodText })}
                  </>
                );
              })()}
              {currentSubscription && (
                <>
                  <br /><br />
                  {t('upgradeConfirm.rollover')}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-neutral-800 hover:bg-neutral-700 text-white border-neutral-700"
              onClick={() => {
                setShowUpgradeConfirm(false);
                setSelectedPlanForUpgrade(null);
              }}
            >
              {t('upgradeConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              onClick={confirmAndUpgrade}
            >
              {t('upgradeConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    , document.body
  );
}
